import { PostgrestError } from "@supabase/supabase-js";
import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import { Database } from "supabase/types";
import logSentryError from "utils/logSentryError";

type DbRoomManager = Database["public"]["Tables"]["room_manager"];
export type RoomManagerRow = DbRoomManager["Row"];
export type RoomManagerInsert = DbRoomManager["Insert"];
export type RoomManagerUpdate = DbRoomManager["Update"];

export const ROOM_MANAGER_ACTIONS = {
  ACTIVATE: "ACTIVATE",
  CANCEL: "CANCEL",
} as const;

export type RoomManagerAction = keyof typeof ROOM_MANAGER_ACTIONS;

// ── Domain types stored in the JSONB `data` column ────────────────────────

export interface RoomManagerZone {
  id: string;
  name: string;
}

export interface RoomManagerRoom {
  id: string;
  name: string;
  /** null means the room does not belong to any zone */
  zoneId: string | null;
  /** null means no capacity limit */
  capacity: number | null;
  /** profile uids of users assigned to this room */
  userIds: number[];
}

export interface RoomManagerData {
  zones: RoomManagerZone[];
  rooms: RoomManagerRoom[];
}

export const EMPTY_ROOM_MANAGER_DATA: RoomManagerData = {
  zones: [],
  rooms: [],
};

export interface GetRoomManagerDataRes {
  data: RoomManagerData;
}

// ── API class ─────────────────────────────────────────────────────────────

class RoomManagerApi {
  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  async activateRoomManager(eventId: number, groupId: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.functions.invoke("room-manager", {
      body: JSON.stringify({ eventId, groupId, action: ROOM_MANAGER_ACTIONS.ACTIVATE }),
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({ message: "Unable to activate Room Manager.", severity: "error" });
      }
      logSentryError(error, "ROOM_MANAGER_API: activateRoomManager");
      return { error };
    }

    return { error: null };
  }

  async cancelRoomManager(eventId: number, groupId: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.functions.invoke("room-manager", {
      body: JSON.stringify({ eventId, groupId, action: ROOM_MANAGER_ACTIONS.CANCEL }),
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({ message: "Unable to cancel Room Manager.", severity: "error" });
      }
      logSentryError(error, "ROOM_MANAGER_API: cancelRoomManager");
      return { error };
    }

    return { error: null };
  }

  async getRoomManagerData(
    eventId: number,
    groupId: number
  ): Promise<{ data: GetRoomManagerDataRes | null; error: PostgrestError | null }> {
    const { data, error } = await supabase
      .from("room_manager")
      .select("data")
      .eq("event_id", eventId)
      .eq("group_id", groupId)
      .maybeSingle();

    if (error) {
      if (this.showNotification) {
        this.showNotification({ message: "Unable to load Room Manager data.", severity: "error" });
      }
      logSentryError(error, "ROOM_MANAGER_API: getRoomManagerData");
      return { data: null, error };
    }

    if (!data) {
      return { data: { data: EMPTY_ROOM_MANAGER_DATA }, error: null };
    }

    return { data: { data: (data.data as unknown as RoomManagerData) ?? EMPTY_ROOM_MANAGER_DATA }, error: null };
  }

  async saveRoomManagerData(
    eventId: number,
    groupId: number,
    roomManagerData: RoomManagerData
  ): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase
      .from("room_manager")
      .update({ data: roomManagerData as any })
      .eq("event_id", eventId)
      .eq("group_id", groupId);

    if (error) {
      if (this.showNotification) {
        this.showNotification({ message: "Unable to save Room Manager data.", severity: "error" });
      }
      logSentryError(error, "ROOM_MANAGER_API: saveRoomManagerData");
      return { error };
    }

    return { error: null };
  }
}

export default RoomManagerApi;
